import { Box } from "@mui/material";
import { useCallback } from "react";
import MedicationEntryCard from "./MedicationEntryCard";
import { useRegisterSectionSave } from "../pending-section-saves-context";
import { useMedicationLookups } from "../hooks/use-medication-lookups";
import { UseMedicationSection } from "../hooks/use-medication-section";
import { REMOVE_ENTRY_LABEL } from "../readmission-f2f-constant";
import {
  ADD_MEDICATION_LABEL,
  SAVE_MEDICATION_LABEL,
  MEDICATION_LABELS,
} from "../readmission-f2f-medication-constant";
import { isMedicationEntryComplete } from "../readmission-f2f-medication-utils";

export interface MedicationEntryListProps {
  medications: UseMedicationSection;
  isEnabled: boolean;
}

const MedicationEntryList = ({
  medications,
  isEnabled,
}: MedicationEntryListProps) => {
  const {
    rows,
    draft,
    isSaving,
    updateRow,
    updateDraft,
    addDraft,
    saveRow,
    removeRow,
    isRowChanged,
  } = medications;
  const lookups = useMedicationLookups(isEnabled);

  // Edited rows and a completed draft are committed when the note is saved or signed.
  const changedRows = rows.filter(
    (row) => isRowChanged(row) && isMedicationEntryComplete(row)
  );
  const isDraftComplete = isMedicationEntryComplete(draft);
  const flush = useCallback(async () => {
    await Promise.all(changedRows.map((row) => saveRow(row)));
    if (isDraftComplete) await addDraft();
  }, [changedRows, isDraftComplete, saveRow, addDraft]);
  useRegisterSectionSave(
    MEDICATION_LABELS.section,
    changedRows.length > 0 || isDraftComplete,
    flush
  );

  const lookupProps = {
    medicineOptions: lookups.medicineOptions,
    isMedicineLoading: lookups.isMedicineLoading,
    onMedicineSearch: lookups.setMedicineSearch,
  };

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {rows.map((row) => (
        <MedicationEntryCard
          key={row.id}
          entry={row}
          {...lookupProps}
          onChange={(patch) => updateRow(row.id, patch)}
          actionLabel={SAVE_MEDICATION_LABEL}
          isActionDisabled={
            isSaving || !isRowChanged(row) || !isMedicationEntryComplete(row)
          }
          onAction={() => saveRow(row)}
          onRemove={() => removeRow(row)}
          removeLabel={`${REMOVE_ENTRY_LABEL} ${row.medicineName}`}
        />
      ))}
      <MedicationEntryCard
        entry={draft}
        isDraft
        {...lookupProps}
        onChange={updateDraft}
        actionLabel={ADD_MEDICATION_LABEL}
        isActionDisabled={isSaving || !isMedicationEntryComplete(draft)}
        onAction={addDraft}
      />
    </Box>
  );
};

export default MedicationEntryList;
